import Cookies from "universal-cookie";

// Rastreamento de uso da Ampli (páginas acessadas, relatórios baixados e ações
// relevantes). Os eventos vão para uma fila e são enviados em lote para
// POST {BACKOFFICE_SERVER_URL}/activity/track. Falhas são silenciosas: o tracking
// nunca pode quebrar ou atrasar a navegação do usuário.
//
// Privacidade: o usuário é identificado só pelo e-mail, que o back-end tira do token.
// Não colocar em metadata nada pessoal ou digitado pelo usuário (buscas, nomes de
// arquivo, textos livres) — apenas identificadores de relatório, obra e formato.

const cookies = new Cookies();

const FLUSH_INTERVAL_MS = 10000;
const MAX_BATCH = 20;
const OBJECT_ID = /\b[a-f0-9]{24}\b/gi;

let queue = [];
let timer = null;
let listenersAttached = false;

const endpoint = () => {
  const base = String(process.env.BACKOFFICE_SERVER_URL || "").replace(
    /\/$/,
    "",
  );
  return base ? `${base}/activity/track` : "";
};

const currentToken = () => cookies.get("userId")?.token;

// "/dashboard/curva/66eacb4976d7c7909b0e9a91" -> "/dashboard/curva/:id", para agrupar
// acessos da mesma página independentemente da obra.
export const normalizeRoute = (pathname = "") =>
  String(pathname).replace(OBJECT_ID, ":id").replace(/\/+$/, "") || "/";

const projectIdFrom = (pathname = "") =>
  String(pathname).match(OBJECT_ID)?.[0] ?? null;

export const flushActivity = async ({
  keepalive = false,
  token = currentToken(),
} = {}) => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const url = endpoint();
  if (!queue.length) return;
  // Sem sessão ou sem endpoint configurado não há como atribuir o evento: descarta.
  if (!url || !token) {
    queue = [];
    return;
  }
  const events = queue.splice(0, queue.length);
  try {
    await fetch(url, {
      method: "POST",
      keepalive,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events }),
    });
  } catch {
    // Silencioso de propósito.
  }
};

const attachListeners = () => {
  if (listenersAttached || typeof window === "undefined") return;
  listenersAttached = true;
  // Ao trocar de aba ou fechar a página, envia o que estiver na fila.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden")
      flushActivity({ keepalive: true });
  });
  window.addEventListener("pagehide", () => flushActivity({ keepalive: true }));
};

export const trackEvent = (type, { path, projectId, metadata } = {}) => {
  if (typeof window === "undefined") return;
  attachListeners();
  const pathname = path ?? window.location.pathname;
  queue.push({
    type,
    path: pathname,
    route: normalizeRoute(pathname),
    projectId: projectId ?? projectIdFrom(pathname),
    metadata: metadata || {},
    at: new Date().toISOString(),
  });
  if (queue.length >= MAX_BATCH) flushActivity();
  else if (!timer) timer = setTimeout(() => flushActivity(), FLUSH_INTERVAL_MS);
};

export const trackPageView = (pathname, metadata) =>
  trackEvent("page_view", { path: pathname, metadata });

// `report` é um identificador estável (snake_case) do relatório; o resto vai em metadata.
export const trackReportDownload = (report, metadata = {}) =>
  trackEvent("report_download", {
    metadata: { report, ...metadata },
  });
